import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, RefreshCw, Check, X } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import DOMPurify from "dompurify";

interface BlogPreviewData {
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  readTime: string;
  suggestedCategory: string;
  featuredImage?: string;
}

interface BlogPreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  previewData: BlogPreviewData | null;
  onAccept: (data: BlogPreviewData) => void;
  onRegenerate: () => void;
  isRegenerating?: boolean;
}

export function BlogPreviewDialog({
  open,
  onOpenChange,
  previewData,
  onAccept,
  onRegenerate,
  isRegenerating = false,
}: BlogPreviewDialogProps) {
  const [editedData, setEditedData] = useState<BlogPreviewData | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  const currentData = isEditing && editedData ? editedData : previewData;

  const handleEdit = () => {
    setEditedData(previewData);
    setIsEditing(true);
  };

  const handleSaveEdits = () => {
    if (editedData) {
      onAccept(editedData);
    }
  };

  const handleAccept = () => {
    if (currentData) {
      onAccept(currentData);
    }
  };

  if (!currentData) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>Review Generated Blog Post</DialogTitle>
          <DialogDescription>
            Review the AI-generated content. You can edit fields or regenerate the entire post.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="preview" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="preview">Preview</TabsTrigger>
            <TabsTrigger value="edit">Edit</TabsTrigger>
          </TabsList>

          <TabsContent value="preview" className="space-y-4">
            <ScrollArea className="h-[500px] pr-4">
              <div className="space-y-6">
                {currentData.featuredImage && (
                  <div className="space-y-2">
                    <Label>Featured Image</Label>
                    <img
                      src={currentData.featuredImage}
                      alt="Featured"
                      className="w-full rounded-lg"
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Title</Label>
                  <p className="text-lg font-semibold">{currentData.title}</p>
                </div>

                <div className="space-y-2">
                  <Label>Slug</Label>
                  <p className="text-sm text-muted-foreground">{currentData.slug}</p>
                </div>

                <div className="space-y-2">
                  <Label>Excerpt</Label>
                  <p className="text-sm">{currentData.excerpt}</p>
                </div>

                <div className="space-y-2">
                  <Label>Read Time</Label>
                  <p className="text-sm">{currentData.readTime}</p>
                </div>

                <div className="space-y-2">
                  <Label>Suggested Category</Label>
                  <p className="text-sm">{currentData.suggestedCategory}</p>
                </div>

                <div className="space-y-2">
                  <Label>Content</Label>
                  <div
                    className="prose prose-sm max-w-none dark:prose-invert"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(currentData.content) }}
                  />
                </div>
              </div>
            </ScrollArea>
          </TabsContent>

          <TabsContent value="edit" className="space-y-4">
            <ScrollArea className="h-[500px] pr-4">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-title">Title</Label>
                  <Input
                    id="edit-title"
                    value={editedData?.title || currentData.title}
                    onChange={(e) =>
                      setEditedData({
                        ...(editedData || currentData),
                        title: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit-slug">Slug</Label>
                  <Input
                    id="edit-slug"
                    value={editedData?.slug || currentData.slug}
                    onChange={(e) =>
                      setEditedData({
                        ...(editedData || currentData),
                        slug: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit-excerpt">Excerpt</Label>
                  <Textarea
                    id="edit-excerpt"
                    value={editedData?.excerpt || currentData.excerpt}
                    onChange={(e) =>
                      setEditedData({
                        ...(editedData || currentData),
                        excerpt: e.target.value,
                      })
                    }
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit-content">Content</Label>
                  <Textarea
                    id="edit-content"
                    value={editedData?.content || currentData.content}
                    onChange={(e) =>
                      setEditedData({
                        ...(editedData || currentData),
                        content: e.target.value,
                      })
                    }
                    rows={15}
                    className="font-mono text-sm"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit-readtime">Read Time</Label>
                  <Input
                    id="edit-readtime"
                    value={editedData?.readTime || currentData.readTime}
                    onChange={(e) =>
                      setEditedData({
                        ...(editedData || currentData),
                        readTime: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
            </ScrollArea>
          </TabsContent>
        </Tabs>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isRegenerating}
          >
            <X className="mr-2 h-4 w-4" />
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={onRegenerate}
            disabled={isRegenerating}
          >
            {isRegenerating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Regenerating...
              </>
            ) : (
              <>
                <RefreshCw className="mr-2 h-4 w-4" />
                Regenerate
              </>
            )}
          </Button>
          <Button
            onClick={isEditing && editedData ? handleSaveEdits : handleAccept}
            disabled={isRegenerating}
          >
            <Check className="mr-2 h-4 w-4" />
            {isEditing && editedData ? "Save & Use" : "Accept & Use"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
